/**
 * Selectors for the live-game store.
 *
 * Every component subscribes through one of these (with `useShallow` for the
 * tuple/object ones) so a message that changes, say, only the clocks cannot
 * re-render the board. Nothing derived is stored twice: captured pieces and
 * the "is it my turn" flag are computed here.
 */
import {Chess} from 'chess.js';

import {CapturedPieces, getCapturedPieces} from './chess/captured';
import {
  GamePhase,
  GameResult,
  LiveGameState,
  START_FEN,
  Side,
  id,
  WATCH_FINISHED_STATE,
} from './state';

export const selectPhase = (s: LiveGameState): GamePhase => s.game.phase;

export const selectMyTurn = (s: LiveGameState): boolean =>
  !!s.session.me && id(s.game.turnUserId) === s.session.me;

export const selectShowAbort = (s: LiveGameState): boolean =>
  s.game.phase === 'playing' && s.game.showAbort;

export type BoardProps = {
  position: string;
  boardOrientation: Side;
  disabled: boolean;
  lastMoveFen: string | null;
  restrictedPiece: any;
  arePremovesAllowed: boolean;
};

/**
 * Fix V3: whether `now` falls inside an armed thinking-time gate for a rule
 * holder in casual play — the exact expression `selectBoardProps` used
 * inline before this fix. Exported so `actions.touchPiece` can ask the same
 * question the board asks (chessground fires `select` even on a disabled
 * board, so a touch during the gate must not be recorded as one) and
 * `hooks/useGameplayEffects` can schedule the gate's expiry as a real store
 * transition instead of leaving it to the next unrelated selector re-run.
 */
/**
 * The thinking-time RULE applies to this game (casual play, rule holder) —
 * independent of whether a gate is running right now.
 */
export const hasThinkingRule = (s: LiveGameState): boolean =>
  s.session.customRules.includes('thinking_time_rule') &&
  s.game.gamePlayMode === 'Play';

export const isThinkingGated = (s: LiveGameState, now: number): boolean =>
  !!s.game.thinkingGateUntil &&
  s.game.thinkingGateUntil > now &&
  hasThinkingRule(s);

/**
 * The exact prop set PlayChess feeds `ChessboardWithArrow` (L4747-4766):
 * disabled while not playing OR while the thinking-time gate is open for a
 * rule holder in casual play OR while another tab/device owns the game
 * (fix E6 — `session.anotherTabActive`, so two tabs cannot race moves).
 */
export const selectBoardProps = (s: LiveGameState): BoardProps => {
  const playing = s.game.phase === 'playing';
  const gated = isThinkingGated(s, Date.now());
  return {
    position: s.game.fen,
    boardOrientation: s.game.myColor === 'black' ? 'black' : 'white',
    disabled: !playing || gated || !!s.session.anotherTabActive,
    lastMoveFen: s.game.previousFen,
    restrictedPiece: s.game.restrictedPiece,
    // Fix W (owner, 2026-10-06): NO premoves under the thinking-time rule. A
    // premove queued on the opponent's turn is replayed by chessground the
    // instant my turn starts — which is exactly when the gate has just been
    // armed and the board is disabled with no dests, so the replay cannot
    // fire, and nothing replays it again once the gate lifts: the premove
    // sat there "stuck" past the end of the thinking time. The rule and
    // premoves are contradictory anyway (the rule exists to make you think
    // before moving), so with the rule on, premoves are simply off.
    arePremovesAllowed: playing && !hasThinkingRule(s),
  };
};

export type ClockView = {
  remainingMs: number;
  runningSince: number | null;
  active: boolean;
};

/**
 * One side's clock. `runningSince` is non-null only for the side to move, so
 * the <Clock> leaf can extrapolate locally without any store tick.
 */
export const selectClock =
  (side: Side) =>
  (s: LiveGameState): ClockView => {
    const sideId = side === 'white' ? s.game.whiteId : s.game.blackId;
    const active =
      s.game.phase === 'playing' &&
      !!s.game.turnUserId &&
      id(s.game.turnUserId) === id(sideId);
    return {
      remainingMs: side === 'white' ? s.game.whiteTimeMs : s.game.blackTimeMs,
      runningSince: active ? s.game.clockRunningSince : null,
      active,
    };
  };

export const selectMoves = (s: LiveGameState): string[] => s.game.moves;

// Captured pieces are a pure function of the FEN; cache the last result so a
// clock-only update does not re-scan the board string.
let capturedCache: {fen: string | null; value: CapturedPieces} = {
  fen: null,
  value: getCapturedPieces(null),
};

export const selectCaptured = (s: LiveGameState): CapturedPieces => {
  if (capturedCache.fen !== s.game.fen) {
    capturedCache = {fen: s.game.fen, value: getCapturedPieces(s.game.fen)};
  }
  return capturedCache.value;
};

export type PlayersView = {
  me: string;
  myColor: Side | null;
  myRating: number | null;
  opponentId: string | null;
  opponentName: string;
  opponentRating: number | null;
  gameDetails: string;
};

export const selectPlayers = (s: LiveGameState): PlayersView => ({
  me: s.session.me,
  myColor: s.game.myColor,
  myRating: s.game.myRating,
  opponentId: s.game.opponentId,
  opponentName: s.game.opponentName,
  opponentRating: s.game.opponentRating,
  gameDetails: s.game.gameDetails,
});

export const selectResult = (s: LiveGameState) => s.game.result;

export const selectLobby = (s: LiveGameState) => s.lobby;

export const selectDrawState = (s: LiveGameState) => ({
  drawOffered: s.game.drawOffered,
  drawSent: s.game.drawSent,
  drawRejectedAt: s.game.drawRejectedAt,
});

/**
 * The Rail state machine (UX spec §3). Pure, cheap (reads 6 fields) and the
 * ONE thing `Body` subscribes to, so exactly one Rail body is ever mounted.
 *
 * M1a implemented the casual-play subset; A3a added the two tournament-family
 * states. `tournament` is the PAGE the old screen calls `showTournamentPlay`
 * (standings / pairings / the arena lobby) and `watch` is the stage state of a
 * spectated game. A tournament or arena GAME is not a state of its own — it is
 * an ordinary `matched`/`live`/`result`, which is why the game reducer applies a
 * tournament spawn like any other. `challengeWait` shares `challengeOut`'s card.
 *
 * An INCOMING challenge does not appear here at all: the app-wide
 * `Components/ChallengeComponent` owns that modal (M2 spec §4bis), so it can
 * arrive mid-game without touching the rail.
 */
export type RailState =
  | 'home'
  | 'tournament'
  | 'seeking'
  | 'challengeOut'
  | 'matched'
  | 'live'
  | 'evalPending'
  | 'result'
  | 'watch';

export const selectRailState = (s: LiveGameState): RailState => {
  // A watch is a STAGE state of its own: it owns the board, so it has to win
  // over the tournament page. It cannot collide with my own game — the old
  // screen clears `watchGame` on both spawn messages (L3539, L3746) and on a
  // pairing broadcast (L3200), and the reducers do the same.
  if (s.watch.active || s.watch.replayGameId) {
    return 'watch';
  }
  if (s.game.phase === 'ended') {
    return s.game.result ? 'result' : tournamentOrHome(s);
  }
  if (s.game.phase === 'evalPending') {
    return 'evalPending';
  }
  if (s.game.phase === 'playing') {
    return 'live';
  }
  if (s.game.phase === 'starting') {
    return 'matched';
  }
  if (s.lobby.phase === 'seeking') {
    return 'seeking';
  }
  if (s.challenge.outgoing) {
    return 'challengeOut';
  }
  return tournamentOrHome(s);
};

/**
 * The tournament PAGE (standings / pairings / the arena lobby, i.e. the old
 * screen's `showTournamentPlay`): a tournament is open, nothing of mine is on
 * the board and no watch is up.
 */
const tournamentOrHome = (s: LiveGameState): RailState =>
  s.tournament.id ? 'tournament' : 'home';

/**
 * The lobby's "Challenge Rejected!" / "Challenge Cancelled!" card. Its own
 * selector (not part of `selectRailState`) because it is a card INSIDE the home
 * panel: the rail state is already back to `home` when it shows.
 */
export const selectChallengeNotice = (s: LiveGameState) => s.challenge.notice;

/**
 * Board orientation = my colour, XOR the user's flip. Pure so the Stage and
 * the BoardFrame can each derive it from their own two subscriptions without
 * passing anything down.
 */
export const orientationFor = (myColor: Side | null, flipped: boolean): Side =>
  (myColor === 'black') !== flipped ? 'black' : 'white';

/**
 * The FEN after `ply` half-moves, replayed from `startFen`. Memoised on the
 * (startFen, moves, ply) triple: browsing the notation must not re-run
 * chess.js on every clock tick.
 */
let plyCache: {
  startFen: string;
  moves: string[];
  ply: number;
  fen: string;
} | null = null;

export const fenAtPly = (
  startFen: string,
  moves: string[],
  ply: number,
): string => {
  if (
    plyCache &&
    plyCache.startFen === startFen &&
    plyCache.moves === moves &&
    plyCache.ply === ply
  ) {
    return plyCache.fen;
  }
  let fen = startFen;
  try {
    const game = new Chess(startFen);
    for (let i = 0; i < ply && i < moves.length; i += 1) {
      game.move(moves[i]);
    }
    fen = game.fen();
  } catch (e) {
    fen = startFen;
  }
  plyCache = {startFen, moves, ply, fen};
  return fen;
};

/**
 * `selectBoardProps` with the MoveList's local browsing applied: while the
 * user is looking at an earlier ply the board shows that position, input and
 * premoves are off, and the last-move highlight is dropped (it belongs to the
 * live position).
 */
export const boardPropsAtPly = (
  s: LiveGameState,
  viewPly: number | null,
): BoardProps => {
  const live = selectBoardProps(s);
  if (viewPly === null || viewPly >= s.game.moves.length) {
    return live;
  }
  return {
    ...live,
    position: fenAtPly(s.game.startFen, s.game.moves, viewPly),
    disabled: true,
    lastMoveFen: null,
    arePremovesAllowed: false,
  };
};

/** lab-only marker; see `selectLiveEvents` and PlayChessV2/lab. */
export const LAB_OPPONENT_DISCONNECTED = '__lab_opponent_disconnected';

/** The inline event cards of `LiveBody` — one subscription for all of them. */
export const selectLiveEvents = (s: LiveGameState) => ({
  drawOffered: s.game.drawOffered,
  drawSent: s.game.drawSent,
  drawRejectedAt: s.game.drawRejectedAt,
  thinkingGateUntil: s.game.thinkingGateUntil,
  opponentName: s.game.opponentName,
  // M1a placeholder: the real opponent-disconnect signal is an M2 protocol
  // question (no message carries it today) — the lab drives it by hand.
  opponentDisconnected: s.meta.lastMessageType === LAB_OPPONENT_DISCONNECTED,
});

/**
 * The first-move countdown chip: shown until the side that OWES the move has
 * played it — white at ply 1, black at ply 2. A consumer applies the same
 * "owed" rule `pickDockEvent` does (`rail/live/dockEvents.ts`), which is why
 * `moves` and `myColor` come along with the deadline.
 */
export const selectFirstMove = (s: LiveGameState) => ({
  deadlineMs: s.game.firstMoveDeadlineMs,
  moves: s.game.moves.length,
  myColor: s.game.myColor,
  isBlackTurnFirst: s.game.isBlackTurnFirst,
});

export type SeatView = {
  name: string;
  rating: number | null;
  /** 'white' | 'black' — what the meta line under the name reads */
  colorLabel: Side;
  isMe: boolean;
};

/**
 * One PlayerRow's identity. Deliberately holds NOTHING that changes per move
 * (no "your move" flag, no clock, no captures): those are separate leaves with
 * their own subscriptions, which is what keeps PlayerRow at 0 renders per
 * opponent move (UX spec §6a acceptance).
 */
export const selectSeat =
  (side: Side) =>
  (s: LiveGameState): SeatView => {
    const seatId = side === 'white' ? s.game.whiteId : s.game.blackId;
    const isMe = !!s.session.me && id(seatId) === s.session.me;
    return {
      name: isMe
        ? s.session.displayName || 'You'
        : s.game.opponentName || 'Opponent',
      rating: isMe ? s.game.myRating : s.game.opponentRating,
      colorLabel: side,
      isMe,
    };
  };

/**
 * Everything the Rail's event dock can show, in ONE subscription.
 *
 * `thinkingGateUntil` is the RULE HOLDER's view of the gate, not the raw timer
 * (P0-4). `game.thinkingGateUntil` is armed unconditionally after move 10,
 * exactly as the old screen armed its own, and the old screen then re-checked
 * `customRules.includes('thinking_time_rule') && gamePlayMode === 'Play'` at
 * every DISPLAY site. `selectBoardProps` does that for the board disable (via
 * `isThinkingGated` above); this is the same check for the dock card /
 * BottomControls, which
 * otherwise showed "Thinking time!" to everyone past move 10.
 */
export const selectDockInput = (s: LiveGameState) => ({
  drawOffered: s.game.drawOffered,
  drawRejectedAt: s.game.drawRejectedAt,
  thinkingGateUntil:
    s.session.customRules.includes('thinking_time_rule') &&
    s.game.gamePlayMode === 'Play'
      ? s.game.thinkingGateUntil
      : null,
  opponentDisconnected: s.meta.lastMessageType === LAB_OPPONENT_DISCONNECTED,
  opponentName: s.game.opponentName,
  firstMoveDeadlineMs: s.game.firstMoveDeadlineMs,
  moves: s.game.moves.length,
  myColor: s.game.myColor,
  isBlackTurnFirst: s.game.isBlackTurnFirst,
});

/**
 * The game is over but the board, the moves and the Rail stay exactly as they
 * were — the result is an OVERLAY on the board column, not a Rail body, so
 * the notation never disappears under it. The toolbar's game actions are
 * disabled from this flag.
 */
export const selectGameOver = (s: LiveGameState): boolean =>
  s.game.phase === 'ended' || s.game.phase === 'evalPending';

export type ResultView = {
  outcome: GameResult['outcome'];
  endType: string;
  message: string;
  myRating: number | null;
  myRatingDelta: number;
  opponentRating: number | null;
  gameUuid: string;
  resultKey: string;
  myName: string;
  myColor: Side | null;
  opponentName: string;
  /**
   * PlayChess's Rematch button, condition for condition (L5203-5205 /
   * L5455-5457: `!(playing || !ChallengeOpponent || !ChallengeMoveTimer) &&
   * started && !hint`). `ChallengeOpponent` / `ChallengeMoveTimer` are seeded
   * from `game_end_response` for EVERY game, quick play included (L3033-3041),
   * so the button is offered after any finished game with a known opponent.
   * `!hint` is the assisted-gameplay hint, i.e. never in Play and Learn.
   */
  canRematch: boolean;
};

/** The one subscription the result overlay needs. */
export const selectResultView = (s: LiveGameState): ResultView | null => {
  const result = s.game.result;
  if (!result) {
    return null;
  }
  return {
    outcome: result.outcome,
    endType: result.endType,
    message: result.message,
    myRating: result.myRating,
    myRatingDelta: result.myRatingDelta,
    opponentRating: result.opponentRating,
    gameUuid: result.gameUuid,
    resultKey: result.key,
    myName: s.session.displayName || 'You',
    myColor: s.game.myColor,
    opponentName: s.game.opponentName || 'Opponent',
    canRematch:
      s.game.phase === 'ended' &&
      !!s.game.opponentId &&
      s.game.tcMs > 0 &&
      !s.game.assisted,
  };
};

// ── Watch (spectating someone else's tournament game) ──────────────────────

/**
 * The watched board's captured trays. Same rule and same one-entry cache as
 * `selectCaptured`: the server sends empty arrays for schema compatibility, so
 * the tray is derived from the watched FEN (PlayChess L3313-3320).
 */
let watchCapturedCache: {fen: string | null; value: CapturedPieces} = {
  fen: null,
  value: getCapturedPieces(null),
};

export const selectWatchCaptured = (s: LiveGameState): CapturedPieces => {
  if (watchCapturedCache.fen !== s.watch.fen) {
    watchCapturedCache = {
      fen: s.watch.fen,
      value: getCapturedPieces(s.watch.fen),
    };
  }
  return watchCapturedCache.value;
};

export type WatchBoardProps = {
  position: string;
  boardOrientation: Side;
  disabled: true;
  lastMoveFen: string | null;
  arePremovesAllowed: false;
};

/**
 * The watched board. A spectator never moves, so it is always disabled and
 * never takes a premove.
 *
 * ORIENTATION: white at the bottom, unless the watcher is himself the black
 * seat of the game he is watching (which happens on the replay path — the old
 * screen reuses the whole `watch*` state to show a FINISHED game of your own,
 * `openArenaGameByTable` L4665-4734).
 */
export const selectWatchBoardProps = (s: LiveGameState): WatchBoardProps => ({
  position: s.watch.fen || s.watch.startFen || START_FEN,
  boardOrientation:
    !!s.session.me && id(s.watch.black?.id) === s.session.me
      ? 'black'
      : 'white',
  disabled: true,
  lastMoveFen: s.watch.prevFen,
  arePremovesAllowed: false,
});

export type WatchSeatView = {
  name: string;
  rating: number | null;
  colorLabel: Side;
  isMe: boolean;
};

/** One watched PlayerRow's identity — the watch twin of `selectSeat`. */
export const selectWatchSeat =
  (side: Side) =>
  (s: LiveGameState): WatchSeatView => {
    const player = side === 'white' ? s.watch.white : s.watch.black;
    return {
      name: player?.name || (side === 'white' ? 'White' : 'Black'),
      rating: player?.rating ?? null,
      colorLabel: side,
      isMe: !!s.session.me && id(player?.id) === s.session.me,
    };
  };

/**
 * One watched clock. It TICKS, like a played one: the old screen hands the
 * watch clocks to the same `SimpleOptimizedClock` with `myTurn={currentPlayer
 * == side}` and `playing={watchState !== 8}` (L6920-6934), so the side to move
 * burns time locally between server messages. State 8 = the game is over and
 * both faces freeze.
 */
export const selectWatchClock =
  (side: Side) =>
  (s: LiveGameState): ClockView => {
    const player = side === 'white' ? s.watch.white : s.watch.black;
    const active =
      s.watch.active &&
      s.watch.state !== WATCH_FINISHED_STATE &&
      !!s.watch.currentPlayer &&
      id(s.watch.currentPlayer) === id(player?.id);
    return {
      remainingMs:
        (side === 'white' ? s.watch.whiteTimeMs : s.watch.blackTimeMs) || 0,
      runningSince: active ? s.watch.clockRunningSince : null,
      active,
    };
  };

/**
 * WHICH GAME the Stage is showing: my own, or one I am spectating.
 *
 * The one selector every `stage/*` model reads before it picks its own source
 * selector (A3b B3). It is a STRING, so a write to the watch slice while nothing
 * is being watched cannot re-render a live leaf — the value simply stays 'live'.
 *
 * `replayGameId` counts as a watch: an arena/Swiss game opened over HTTP is
 * rendered through the very same slice (PlayChess `openArenaGameByTable`
 * L4665-4734), it just never ticks.
 */
export type BoardSource = 'live' | 'watch';

export const selectBoardSource = (s: LiveGameState): BoardSource =>
  s.watch.active || s.watch.replayGameId ? 'watch' : 'live';

export const selectWatchMoves = (s: LiveGameState): string[] => s.watch.moves;

export const selectWatchEnded = (s: LiveGameState) => s.watch.ended;

// ── Tournament / arena ─────────────────────────────────────────────────────

/**
 * The game-state mirror `Screens/CCTournaments/components/TournamentPlay` reads
 * (its props at PlayChess L7844-7885). ONE `useShallow` object so the whole
 * tournament page re-renders at most once per message instead of once per prop,
 * and every value is derived here rather than in the component.
 *
 * Two shape notes, both recorded in PARITY §9bis:
 *  - the clocks are MILLISECONDS (the old props are seconds — this core keeps
 *    every duration in ms and the consumer formats);
 *  - `moves` / `tournamentMoves` are SAN STRING lists. The old screen passes
 *    `{move, matched, isHumanMove}` objects and prepends `{}` when black moved
 *    first; that shaping is the VIEW's, so it stays on the app side.
 */
export type TournamentPlayBridge = {
  moves: string[];
  myTime: number;
  opponentTime: number;
  myTurn: boolean;
  /** the white seat's user id, which is what `boardOrientation` compares */
  white: string | null;
  opponent: string;
  captured: CapturedPieces;
  myRating: number | null;
  opponentRating: number | null;
  position: string;
  startfen: string;
  game_uuid: string;
  playing: boolean;
  isBlackTurnFirst: boolean;
  tournamentMoves: string[];
  startfenTournament: string;
};

export const selectTournamentPlayBridge = (
  s: LiveGameState,
): TournamentPlayBridge => {
  const iAmWhite = !!s.session.me && id(s.game.whiteId) === s.session.me;
  return {
    moves: s.game.moves,
    myTime: iAmWhite ? s.game.whiteTimeMs : s.game.blackTimeMs,
    opponentTime: iAmWhite ? s.game.blackTimeMs : s.game.whiteTimeMs,
    myTurn: selectMyTurn(s),
    white: s.game.whiteId,
    opponent: s.game.opponentName,
    captured: selectCaptured(s),
    myRating: s.game.myRating,
    opponentRating: s.game.opponentRating,
    position: s.game.fen,
    startfen: s.game.startFen,
    game_uuid: s.game.result?.gameUuid || '',
    playing: s.game.phase === 'playing',
    isBlackTurnFirst: s.game.isBlackTurnFirst,
    tournamentMoves: s.watch.moves,
    startfenTournament: s.watch.startFen || START_FEN,
  };
};

/**
 * Lichess parity: a JOINED idle player is re-pooled automatically — at most
 * once per tournament visit (`autoResumed`, re-armed when the tournament id
 * changes) so it cannot loop, never for a PAUSED player and never for a mere
 * spectator (PlayChess L4745-4763). Refresh therefore keeps a paused player
 * paused.
 */
export const selectArenaAutoResumeDue = (s: LiveGameState): boolean =>
  s.tournament.mode === 'arena' &&
  !!s.tournament.id &&
  s.arena.isJoined &&
  s.arena.status?.state === 'running' &&
  s.arena.status?.myState === 'idle' &&
  !s.arena.autoResumed;

/** the berserk badges + the refusal the screen has to voice. */
export const selectArenaBerserk = (s: LiveGameState) => ({
  myBerserk: s.arena.myBerserk,
  opponentBerserk: s.arena.opponentBerserk,
  lastGameBerserk: s.arena.lastGameBerserk,
  refusal: s.arena.berserkRefusal,
});

/** the tournament page's header state, in one subscription. */
export const selectTournamentPage = (s: LiveGameState) => ({
  id: s.tournament.id,
  mode: s.tournament.mode,
  roundState: s.tournament.roundState,
  statusMessage: s.tournament.statusMessage,
  ongoingGames: s.tournament.ongoingGames,
  currentRound: s.tournament.currentRound,
  roundStartAt: s.tournament.roundStartAt,
  nextRound: s.tournament.nextRound,
  playersJoined: s.tournament.playersJoined,
  ended: s.tournament.ended,
  pairingGenerated: s.tournament.pairingGenerated,
  gamesEnded: s.tournament.gamesEnded,
});

export const selectTournamentJoin = (s: LiveGameState) => s.tournament.join;
